/**
 * useTheme — Product UI Standard §7 theme switching primitive.
 *
 * T4_6-003 pattern parent. Manages the `[data-theme]` attribute on `<html>`
 * that toggles the Phase 4.6 color cascade defined in
 * `packages/shared/src/tokens/colors.css`. Three states:
 *
 *   - `'system'` — defer to OS preference via `prefers-color-scheme`. Default
 *     for first-load users; reflects the user not having made an explicit
 *     choice yet.
 *   - `'light'` — explicit light. `data-theme` set to `'light'`.
 *   - `'dark'`  — explicit dark.  `data-theme` set to `'dark'`.
 *
 * Persistence: localStorage key `rr.theme` (Standard §7.3). When set to
 * `'system'`, the key is removed; missing key = system mode.
 *
 * The hook is **module-singleton-backed via `useSyncExternalStore`** —
 * every consumer (nav user-chip toggle, sandbox toggle, future settings
 * page toggle) reads the same state without React context. Setting from
 * one consumer notifies every other consumer in the same render cycle.
 *
 * Standard §7.3 also requires a 150ms ease transition on color properties
 * during theme switch (no layout shift). That transition is declared in
 * `packages/shared/src/tokens/colors.css` as a global `:root` rule shipped
 * by this ticket; consumers don't need per-component transition styling.
 */
import { useCallback, useSyncExternalStore } from 'react';
const STORAGE_KEY = 'rr.theme';
const VALID_MODES = new Set(['light', 'dark', 'system']);
/**
 * Read the user's persisted preference. Missing or invalid value falls
 * back to `'system'` per Standard §7.3 ("System preference detected at
 * first load via `prefers-color-scheme`").
 */
function readStoredMode() {
    if (typeof window === 'undefined')
        return 'system';
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw && VALID_MODES.has(raw))
            return raw;
    }
    catch {
        // localStorage can throw in sandboxed iframes / certain privacy modes;
        // treat as system mode.
    }
    return 'system';
}
/**
 * Resolve the active rendered theme. `'system'` reads `prefers-color-scheme`
 * at call time; `'light'` / `'dark'` map literally. The Phase 4.6 tokens
 * cascade on `[data-theme="dark"]`; the absence of the attribute (or
 * `[data-theme="light"]`) renders the light palette.
 */
function resolveEffectiveTheme(mode) {
    if (mode === 'dark' || mode === 'light')
        return mode;
    if (typeof window === 'undefined' || !window.matchMedia)
        return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
/**
 * Apply the effective theme to `<html>`. Idempotent — re-setting the same
 * value is a no-op for the DOM. Hero pages (Standard §6) keep their own
 * always-dark surface via component-level CSS; this attribute only governs
 * the cascade for non-hero surfaces.
 */
function applyTheme(effective) {
    if (typeof document === 'undefined')
        return;
    const root = document.documentElement;
    if (effective === 'dark') {
        root.setAttribute('data-theme', 'dark');
    }
    else {
        // Light mode: REMOVE the attribute instead of setting it to 'light'.
        // The light palette is the default branch in colors.css (the unmarked
        // `:root` rule); the dark cascade is the `[data-theme="dark"]` rule.
        // Removing the attribute is the canonical "go back to light" path.
        root.removeAttribute('data-theme');
    }
}
/**
 * Module-singleton state. Holds the current mode (what the user picked) +
 * derived effective theme (what's actually rendered). Subscribers notify
 * on either changing.
 */
let currentMode = readStoredMode();
let currentEffective = resolveEffectiveTheme(currentMode);
const subscribers = new Set();
function notify() {
    // Same-page consumers re-render via the subscriber set. Genuine cross-tab
    // sync (another tab of the same origin) is handled by the `storage` event
    // listener in initOnce() — not by a custom event. (RIG-298 removed a
    // `rr:theme-changed` CustomEvent dispatch here that had no listener
    // anywhere in the monorepo — a dead broadcast.)
    for (const cb of subscribers)
        cb();
}
/**
 * Initialize listeners that keep `currentEffective` in sync with OS-level
 * changes (only relevant when mode === 'system') and with cross-tab
 * localStorage changes. Idempotent — guarded by a one-shot flag.
 */
let initialized = false;
function initOnce() {
    if (initialized || typeof window === 'undefined')
        return;
    initialized = true;
    // Apply the initial state to the DOM. Without this the first render of
    // any consumer would see the document still in its un-themed state.
    applyTheme(currentEffective);
    // OS-level dark mode changes (mode === 'system' consumers care).
    if (window.matchMedia) {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
            if (currentMode !== 'system')
                return;
            const next = resolveEffectiveTheme('system');
            if (next === currentEffective)
                return;
            currentEffective = next;
            applyTheme(currentEffective);
            notify();
        });
    }
    // Cross-tab storage changes (user toggled theme in another tab of the
    // same origin — e.g., the user has both staging-crm and staging-portal
    // open and changes the mode in one).
    window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY)
            return;
        const next = readStoredMode();
        if (next === currentMode)
            return;
        currentMode = next;
        currentEffective = resolveEffectiveTheme(currentMode);
        applyTheme(currentEffective);
        notify();
    });
}
function subscribe(cb) {
    initOnce();
    subscribers.add(cb);
    return () => {
        subscribers.delete(cb);
    };
}
/**
 * Snapshot returned by useSyncExternalStore. Reference-stable while
 * underlying state is unchanged — required for the React reconciler to
 * skip re-renders on no-op subscriber notifications.
 */
let cachedSnapshot = { mode: currentMode, effective: currentEffective };
function getSnapshot() {
    if (cachedSnapshot.mode !== currentMode || cachedSnapshot.effective !== currentEffective) {
        cachedSnapshot = { mode: currentMode, effective: currentEffective };
    }
    return cachedSnapshot;
}
/**
 * Server-side snapshot. `useSyncExternalStore` requires this when consumed
 * in SSR contexts (currently unused — V1 CRM/Portal/Intake are SPA — but
 * future Next.js / RSC migrations of the marketing site would consume it).
 * Defaults to light because the un-themed cascade IS light.
 */
function getServerSnapshot() {
    return { mode: 'system', effective: 'light' };
}
/**
 * Set the user's theme preference. Persists to localStorage (or removes
 * the key for `'system'` mode), updates the `[data-theme]` attribute on
 * `<html>`, and notifies all subscribers.
 *
 * Exposed as a standalone export so non-React consumers (boot-time
 * initialization in `main.tsx`; tests) can drive theme without rendering
 * the hook.
 */
export function setTheme(next) {
    if (!VALID_MODES.has(next))
        return;
    if (next === currentMode)
        return;
    currentMode = next;
    currentEffective = resolveEffectiveTheme(next);
    applyTheme(currentEffective);
    if (typeof window !== 'undefined') {
        try {
            if (next === 'system') {
                window.localStorage.removeItem(STORAGE_KEY);
            }
            else {
                window.localStorage.setItem(STORAGE_KEY, next);
            }
        }
        catch {
            // Tolerate localStorage failures (sandboxed iframes / quota); the
            // in-memory module state still drives the DOM correctly within
            // this session.
        }
    }
    notify();
}
/**
 * React hook returning the current theme state + setter. Three-tuple-ish
 * return for explicit destructure:
 *
 *   const { mode, effective, setTheme } = useTheme();
 *
 * `mode` is the user's preference (what the toggle UI should show as
 * selected); `effective` is what's actually rendered (drives any
 * conditional renders that need to behave differently between light and
 * dark when the user is in `'system'` mode).
 */
export function useTheme() {
    const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const stableSetTheme = useCallback((next) => setTheme(next), []);
    return {
        mode: snapshot.mode,
        effective: snapshot.effective,
        setTheme: stableSetTheme,
    };
}
/**
 * Module-load side effect: apply the persisted theme to the DOM as
 * early as possible. Without this, the first paint after page load
 * happens in the (light) un-themed state and flickers to dark on first
 * `useTheme()` mount. This call runs at module evaluation; `initOnce`
 * is idempotent so the same work via `subscribe()` is a no-op later.
 */
initOnce();
//# sourceMappingURL=useTheme.js.map